<template>
  <el-menu
    :default-active="$route.path"
    class="modern-menu"
    :collapse="collapsed"
    background-color="transparent"
    text-color="#a6adb4"
    active-text-color="#ffffff"
    router
  >
    <!-- Dashboard -->
    <el-menu-item index="/" v-if="userType !== 'cashier'">
      <el-icon><HomeFilled /></el-icon>
      <template #title><span>{{ $t('dashboard') }}</span></template>
    </el-menu-item>

    <!-- User Management Section -->
    <el-sub-menu index="user-management" v-if="showUserManagement && userType !== 'cashier'">
      <template #title>
        <el-icon><User /></el-icon>
        <span>{{ $t('studentAffierEmployee') }}</span>
      </template>
      <el-menu-item v-if="canAccessStudents" index="/students">{{ $t('studentsManagement') }}</el-menu-item>
      <el-menu-item v-if="canAccessParents" index="/parents">{{ $t('parentsManagement') }}</el-menu-item>
      <el-menu-item v-if="canAccessEmployees" index="/employees">{{ $t('employeesManagement') }}</el-menu-item>
      <el-menu-item v-if="canAccessEmployees" index="/parents-account">{{ $t('parentsAccounts') }}</el-menu-item>
      <el-menu-item v-if="canAccessEmployees" index="/teachers-account">{{ $t('teachersAccounts') }}</el-menu-item>
      <el-menu-item v-if="canAccessEmployees" index="/student-id-cards">{{ $t('studentIdCards') }}</el-menu-item>
      <el-menu-item v-if="canAccessStudentsCards" index="/cards-students">{{ $t('studentsCards') }}</el-menu-item>
    </el-sub-menu>

    <!-- Academic Management Section -->
    <el-sub-menu index="academic-management" v-if="showAcademicSection && userType !== 'cashier'">
      <template #title>
        <el-icon><Reading /></el-icon>
        <span>{{ $t('academicManagement') }}</span>
      </template>
      <el-menu-item v-if="showSubjects" index="/subjects">{{ $t('subjects') }}</el-menu-item>
      <el-menu-item v-if="showSections" index="/classes-sections">{{ $t('sectionsOfClassManagement') }}</el-menu-item>
      <el-menu-item v-if="canAccessAttendance" index="/attendance">{{ $t('attendance') }}</el-menu-item>
      <el-menu-item v-if="canAccessExams" index="/exams">{{ $t('exams') }}</el-menu-item>
      <el-menu-item v-if="canAccessAcademicSchedule" index="/academic-schedule">{{ $t('academicSchedule') }}</el-menu-item>
      <el-menu-item v-if="canAccessGenerateGroups" index="/generate-groups">{{ $t('groups') }}</el-menu-item>
    </el-sub-menu>
    <el-sub-menu index="academic-teacher-management" v-if="showTeacherAcademicSection && userType !== 'cashier'">
      <template #title>
        <el-icon><Reading /></el-icon>
        <span>{{ $t('academicManagement') }}</span>
      </template>

      <el-menu-item v-if="showSubjects" index="/my-assignment">{{ $t('myAssignment') }}</el-menu-item>
    </el-sub-menu>

    <!-- Financial Management Section -->
    <el-sub-menu index="financial-management" v-if="showFinancialSection && userType !== 'cashier'">
      <template #title>
        <el-icon><Money /></el-icon>
        <span>{{ $t('financialManagement') }}</span>
      </template>
      <el-menu-item v-if="showFinancialDoc && userType !== 'cashier'" index="/invoice-management">{{ $t('invoiceManagement') }}</el-menu-item>
      <el-menu-item v-if="showFinancialDoc && userType !== 'cashier'" index="/invoice-list">{{ $t('invoiceList') }}</el-menu-item>
      <el-menu-item v-if="showTreasury" index="/treasury">{{ $t('treasury') }}</el-menu-item>
      <el-menu-item v-if="canAccessFinancialReports" index="/financial-reports">{{ $t('financialReports') }}</el-menu-item>
      <el-menu-item v-if="canAccessSalaries" index="/salaries">{{ $t('salaries') }}</el-menu-item>
    </el-sub-menu>
    
    <!-- System Settings Section -->
    <el-sub-menu index="system-settings" v-if="showSystemSection && userType !== 'cashier'">
      <template #title>
        <el-icon><Setting /></el-icon>
        <span>{{ $t('systemSettings') }}</span>
      </template>
      <el-menu-item v-if="showDepartments" index="/study-years">{{ $t('studyYear') }}</el-menu-item>
      <el-menu-item v-if="showClasses" index="/classes">{{ $t('classesManagement') }}</el-menu-item>
      <el-menu-item v-if="canAccessDepartments" index="/departments">{{ $t('departments') }}</el-menu-item>
      <el-menu-item v-if="canAccessUsers" index="/users">{{ $t('users') }}</el-menu-item>
      <el-menu-item v-if="showSalaries" index="/announcement-sender">{{ $t('announcementSender') }}</el-menu-item>
      <el-menu-item v-if="showSalaries" index="/notification-sender-parents">{{ $t('notificationSender') }}</el-menu-item>
      <el-menu-item v-if="showSalaries" index="/user-notification">{{ $t('userNotification') }}</el-menu-item>
    </el-sub-menu>

    <!-- Health & Kitchen Section -->
    <el-sub-menu index="health-kitchen" v-if="showHealthKitchenSection">
      <template #title>
        <el-icon><Setting /></el-icon>
        <span>{{ $t('healthKitchen') }}</span>
      </template>
      <el-menu-item v-if="canAccessHealthFiles && userType !== 'cashier'" index="/health-files">{{ $t('healthFile') }}</el-menu-item>
      <el-menu-item v-if="canAccessKitchen && userType !== 'cashier'" index="/kitchen">{{ $t('kitchen') }}</el-menu-item>
      <el-menu-item v-if="canAccessKitchenSales" index="/kitchen-sales">{{ $t('kitchenSales') }}</el-menu-item>
    </el-sub-menu>

  </el-menu>
</template>

<script>
import { mapGetters } from 'vuex';
import { HomeFilled, User, Reading, Money, Setting } from '@element-plus/icons-vue';

export default {
  name: "SidenavList",
  props: {
    collapsed: Boolean,
  },
  components: {
    HomeFilled, User, Reading, Money, Setting
  },
  computed: {
    ...mapGetters(['userType','username']),
    
    /**
     * Check if user has permission for a route based on allowedRoles
     */
    hasPermission() {
      return (allowedRoles) => {
        if (!allowedRoles || allowedRoles.length === 0) return true;
        return allowedRoles.includes(this.userType);
      };
    },
    
    // Section visibility - based on router allowedRoles
    showUserManagement() { 
      return this.hasPermission(['moder', 'admin', 'hr', 'student_m', 'prin', 'cashier', 'student supervisor', 'developer']); 
    },
    showAcademicSection() { 
      return this.hasPermission(['moder', 'admin', 'student_m', 'prin', 'hr', 'teacher', 'developer']); 
    },
    showTeacherAcademicSection() { 
      return this.hasPermission(['teacher']); 
    },
    showFinancialSection() { 
      return this.hasPermission(['moder', 'financial', 'hr', 'cashier', 'developer']) && this.username !== 'academic_cis'; 
    },
    showSystemSection() { 
      return this.hasPermission(['moder', 'admin', 'hr', 'prin', 'developer']); 
    },
    showHealthKitchenSection() { 
      return this.hasPermission(['moder', 'doctor', 'financial', 'hr', 'cashier', 'student supervisor', 'developer']); 
    },
    showSummerCampSection() { 
      return this.hasPermission(['moder', 'hr', 'prin', 'developer']); 
    },
    
    // Individual item visibility - based on router allowedRoles
    showStudents() { 
      return this.hasPermission(['hr', 'prin', 'moder', 'student_m', 'developer']); 
    },
    showParents() { 
      return this.hasPermission(['admin', 'student_m', 'prin', 'moder', 'developer']); 
    },
    showEmployees() { 
      return this.hasPermission(['admin', 'hr', 'moder', 'developer']); 
    },
    showSubjects() { 
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer', 'teacher']); 
    },
    showClasses() { 
      return this.hasPermission(['moder', 'student_m', 'hr', 'admin', 'developer']); 
    },
    showSections() { 
      return this.hasPermission(['prin', 'hr', 'student_m', 'moder', 'developer']); 
    },
    showDepartments() { 
      return this.hasPermission(['admin', 'hr', 'moder', 'prin', 'developer']); 
    },
    showFinancialDoc() { 
      return this.hasPermission(['financial', 'hr', 'moder', 'cashier', 'developer']); 
    },
    showTreasury() { 
      return this.hasPermission(['financial', 'moder', 'developer']); 
    },
    showSalaries() { 
      return this.hasPermission(['financial', 'hr', 'moder', 'cashier', 'developer']); 
    },
    showChartOfAccounts() {
      return this.showSalaries && this.username !== 'academic_cis';
    },
    showHealth() { 
      return this.hasPermission(['moder', 'doctor', 'prin', 'student_m', 'developer']); 
    },
    showKitchen() { 
      return this.hasPermission(['moder', 'financial', 'cashier', 'developer']); 
    },
    
    // Specific route permissions
    canAccessStudents() {
      return this.hasPermission(['hr', 'prin', 'moder', 'student_m', 'developer']);
    },
    canAccessParents() {
      return this.hasPermission(['admin', 'student_m', 'prin', 'moder', 'developer']);
    },
    canAccessEmployees() {
      return this.hasPermission(['admin', 'hr', 'moder', 'developer']);
    },
    canAccessBuses() {
      return this.hasPermission(['moder', 'student_m', 'hr', 'admin', 'developer']);
    },
    canAccessExams() {
      return this.hasPermission(['hr', 'prin', 'moder', 'student_m', 'developer']);
    },
    canAccessReportCards() {
      return this.hasPermission(['hr', 'prin', 'moder', 'student_m', 'developer']);
    },
    canAccessTimeTable() {
      return this.hasPermission(['hr', 'prin', 'moder', 'student_m', 'developer']);
    },
    canAccessAcademicSchedule() {
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer']);
    },
    canAccessAttendance() {
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'teacher', 'developer']);
    },
    canAccessAttendanceList() {
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'teacher', 'developer']);
    },
    canAccessAssignSubjects() {
      return this.hasPermission(['hr', 'prin', 'moder', 'financial', 'developer']);
    },
    canAccessStudentAttendances() {
      return this.hasPermission(['hr', 'prin', 'moder', 'teacher', 'student_m', 'developer']);
    },
    canAccessStudentReport() {
      return this.hasPermission(['hr', 'prin', 'moder', 'student_m', 'developer']);
    },
    canAccessStudentsCards() {
      return this.hasPermission(['hr', 'student_m', 'moder', 'developer']);
    },
    canAccessTreasuryTransactions() {
      return this.hasPermission(['moder', 'developer']);
    },
    canAccessVendors() {
      return this.hasPermission(['moder', 'hr', 'prin', 'financial', 'developer']);
    },
    canAccessBills() {
      return this.hasPermission(['moder', 'hr', 'prin', 'financial', 'developer']);
    },
    canAccessFinancialReports() {
      return this.hasPermission(['moder', 'hr', 'prin', 'financial', 'developer']);
    },
    canAccessParentWallet() {
      return this.hasPermission(['hr', 'prin', 'moder', 'financial', 'developer']);
    },
    canAccessParentFinancialProfile() {
      return this.hasPermission(['hr', 'financial', 'moder', 'developer']);
    },
    canAccessSalaries() {
      return this.hasPermission(['financial', 'moder', 'hr', 'developer']);
    },
    canAccessManageSalaries() {
      return this.hasPermission(['financial', 'moder', 'hr', 'developer']);
    },
    canAccessSalaryShow() {
      return this.hasPermission(['hr', 'financial', 'moder', 'developer']);
    },
    canAccessSubscriptionFee() {
      return this.hasPermission(['hr', 'financial', 'moder', 'developer']);
    },
    canAccessSiblingDiscount() {
      return this.hasPermission(['hr', 'financial', 'moder', 'developer']);
    },
    canAccessEmployeeWallets() {
      return this.hasPermission(['moder', 'financial', 'developer']);
    },
    canAccessEmployeeHourSalary() {
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer']);
    },
    canAccessAttendanceHourEmployee() {
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer']);
    },
    canAccessDeductionPerHour() {
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer']);
    },
    canAccessIssuedSalariesPerHour() {
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer']);
    },
    canAccessAbsenceDeduction() {
      return this.hasPermission(['moder', 'hr', 'prin', 'financial', 'developer']);
    },
    canAccessEmployeeReport() {
      return this.hasPermission(['hr', 'prin', 'moder', 'financial', 'developer']);
    },
    canAccessKitchen() {
      return this.hasPermission(['moder', 'financial', 'developer']);
    },
    canAccessKitchenSales() {
      return this.hasPermission(['moder', 'cashier', 'developer']);
    },
    canAccessAllKitchenBills() {
      return this.hasPermission(['moder', 'cashier', 'developer']);
    },
    canAccessStudentRestrictedMeals() {
      return this.hasPermission(['moder', 'financial', 'student_m', 'doctor', 'hr', 'developer']);
    },
    canAccessStudentPurchaseLimit() {
      return this.hasPermission(['moder', 'financial', 'student_m', 'hr', 'developer']);
    },
    canAccessHealthFiles() {
      return this.hasPermission(['moder', 'doctor', 'developer']);
    },
    canAccessAllHealthFiles() {
      return this.hasPermission(['moder', 'doctor', 'prin', 'student_m', 'developer']);
    },
    canAccessDepartments() {
      return this.hasPermission(['hr', 'prin', 'moder', 'developer']);
    },
    canAccessEmployeeType() {
      return this.hasPermission(['moder', 'hr', 'prin', 'developer']);
    },
    canAccessPermissionEmployee() {
      return this.hasPermission(['hr', 'prin', 'moder', 'developer']);
    },
    canAccessEmployeeDetails() {
      return this.hasPermission(['hr', 'prin', 'moder', 'developer']);
    },
    canAccessTeacherType() {
      return this.hasPermission(['hr', 'prin', 'moder', 'developer']);
    },
    canAccessUsers() {
      return this.hasPermission(['moder', 'developer']);
    },
    canAccessRegisterUsers() {
      return this.hasPermission(['hr', 'prin', 'moder', 'student_m', 'developer']);
    },
    canAccessSendEmails() {
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer']);
    },
    canAccessEventsCalendar() {
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer']);
    },
    canAccessStories() {
      return this.hasPermission(['hr', 'prin', 'moder', 'financial', 'developer']);
    },
    canAccessActivityLog() {
      return this.hasPermission(['moder', 'developer']);
    },
    canAccessLogsCheck() {
      return this.hasPermission(['hr', 'financial', 'moder', 'developer']);
    },
    canAccessImportAttendance() {
      return this.hasPermission(['moder', 'financial', 'hr', 'developer']);
    },
    canAccessVerifyParentIdentity() {
      return this.hasPermission(['moder', 'hr', 'prin', 'admin', 'developer']);
    },
    canAccessSummerCampAdd() {
      return this.hasPermission(['hr', 'prin', 'moder', 'developer']);
    },
    canAccessSummerCampData() {
      return this.hasPermission(['hr', 'prin', 'moder', 'developer']);
    },
    canAccessGenerateGroups() {
      return this.hasPermission(['hr', 'prin', 'moder', 'financial', 'developer', 'teacher']);
    },
  },
};
</script>

<style scoped>
.modern-menu {
  border-right: none !important;
  background: transparent !important;
}

.modern-menu:not(.el-menu--collapse) {
  width: 100%;
}

/* Menu items styling */
.modern-menu :deep(.el-menu-item),
.modern-menu :deep(.el-sub-menu__title) {
  height: 48px;
  line-height: 48px;
  margin: 4px 8px;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.modern-menu :deep(.el-menu-item:hover),
.modern-menu :deep(.el-sub-menu__title:hover) {
  background: rgba(102, 126, 234, 0.12) !important;
  color: #ffffff !important;
  transform: translateX(-2px);
}

.modern-menu :deep(.el-menu-item.is-active) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  font-weight: 600;
}

.modern-menu :deep(.el-menu-item.is-active::before) {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #ffffff;
  border-radius: 0 3px 3px 0;
}

/* Sub-menu styling */
.modern-menu :deep(.el-sub-menu) {
  margin: 4px 8px;
}

.modern-menu :deep(.el-sub-menu .el-menu-item) {
  margin: 2px 0;
  padding-left: 48px !important;
  height: 44px;
  line-height: 44px;
}

.modern-menu :deep(.el-sub-menu .el-menu-item:hover) {
  background: rgba(102, 126, 234, 0.08) !important;
  padding-left: 52px !important;
}

.modern-menu :deep(.el-sub-menu .el-menu-item.is-active) {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%) !important;
  border-left: 3px solid #667eea;
  padding-left: 45px !important;
}

/* Icons styling */
.modern-menu :deep(.el-menu-item i),
.modern-menu :deep(.el-sub-menu__title i) {
  font-size: 20px;
  margin-right: 12px;
  transition: transform 0.3s ease;
}

.modern-menu :deep(.el-menu-item:hover i),
.modern-menu :deep(.el-sub-menu__title:hover i) {
  transform: scale(1.1);
}

/* Collapsed menu */
.modern-menu.el-menu--collapse {
  width: 100%;
}

.modern-menu.el-menu--collapse :deep(.el-menu-item),
.modern-menu.el-menu--collapse :deep(.el-sub-menu__title) {
  padding: 0 20px !important;
  justify-content: center;
}

/* Smooth transitions */
.modern-menu :deep(*) {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
